<header class="flex header">
  <h2>
    {{ 'ApDocument_ReceivedCoverage_Title' | translateWF: 'Received vs. paid' }}
    @if (coverage.documentNumber) {
      · {{ coverage.documentNumber }}
    }
  </h2>
  <p-button icon="pi pi-times" (click)="onClose()" />
</header>

<section class="content">
  <loading-overlay [isLoading]="isLoading()"></loading-overlay>

  <p class="text-sm text-[var(--p-text-muted-color)] mb-3">
    {{
      'ApDocument_ReceivedCoverage_Intro'
        | translateWF: 'Checked per purchase order, before tax: what has been paid on the PO plus what is being paid now must not exceed what was received.'
    }}
  </p>

  <p-tabs [value]="activeTab()" (valueChange)="onTabChange($event)" [scrollable]="true">
    <p-tablist>
      @for (po of purchaseOrders(); track po.purchaseOrderReference) {
        <p-tab [value]="po.purchaseOrderReference">
          @if (!po.checked) {
            <i class="pi pi-question-circle mr-2 text-[var(--p-text-muted-color)]"></i>
          } @else if (po.shortfall > 0) {
            <i class="pi pi-exclamation-triangle mr-2 text-[var(--p-orange-500)]"></i>
          } @else {
            <i class="pi pi-check-circle mr-2 text-[var(--p-green-500)]"></i>
          }
          {{ po.purchaseOrderNumber ?? '—' }}
        </p-tab>
      }
    </p-tablist>

    <p-tabpanels>
      @for (po of purchaseOrders(); track po.purchaseOrderReference) {
        <p-tabpanel [value]="po.purchaseOrderReference">
          @let unit = currencyLabel(po.currency);

          <div class="flex justify-end mb-2">
            <!-- A new tab, so reading the PO's full history never costs the request or payment in progress. -->
            <a
              pButton
              [label]="'ApDocument_ReceivedCoverage_OpenSettlement' | translateWF: 'Open PO settlement'"
              icon="pi pi-arrow-up-right"
              iconPos="right"
              [text]="true"
              [routerLink]="['/po-settlement-details']"
              [queryParams]="{ id: po.purchaseOrderReference }"
              target="_blank"
              rel="noopener"
            ></a>
          </div>

          @if (!po.checked) {
            <p-message severity="secondary" styleClass="w-full">
              {{
                'ApDocument_ReceivedCoverage_Unchecked'
                  | translateWF: 'This PO could not be checked: it was not found, or its documents are in different currencies and a receipt has no exchange rate for its day.'
              }}
            </p-message>
          } @else {
            <!-- The check's own figures, exactly as the warning used them. -->
            @if (po.comparedInFunctional) {
              <p-message severity="info" styleClass="w-full mb-3">
                {{ 'ApDocument_ReceivedShortfall_Functional' | translateWF: '(functional currency: a document on this PO is in another currency)' }}
              </p-message>
            }
            <div class="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
              <div class="form-box">
                <label>{{ 'ApDocument_ReceivedCoverage_Received' | translateWF: 'Received (net of returns)' }}</label>
                <span class="tabular-nums">{{ po.netReceived | number: '1.2-2' }} {{ unit }}</span>
              </div>
              <div class="form-box">
                <label>{{ 'ApDocument_ReceivedCoverage_PaidToDate' | translateWF: 'Paid to date' }}</label>
                <span class="tabular-nums">{{ po.paidToDate | number: '1.2-2' }} {{ unit }}</span>
              </div>
              <div class="form-box">
                <label>{{ 'ApDocument_ReceivedCoverage_ThisDocument' | translateWF: 'This document' }}</label>
                <span class="tabular-nums">{{ po.payingNow | number: '1.2-2' }} {{ unit }}</span>
              </div>
              <div class="form-box">
                <label>{{ 'ApDocument_ReceivedCoverage_OthersInBatch' | translateWF: 'Others being paid now' }}</label>
                <span class="tabular-nums">{{ po.payingWithOthers | number: '1.2-2' }} {{ unit }}</span>
              </div>
              <div class="form-box">
                <label>{{ 'ApDocument_ReceivedCoverage_Over' | translateWF: 'Over by' }}</label>
                @if (po.shortfall > 0) {
                  <p-tag severity="warn" [value]="(po.shortfall | number: '1.2-2') + ' ' + unit" />
                } @else {
                  <p-tag severity="success" [value]="'ApDocument_ReceivedCoverage_Within' | translateWF: 'Within received'" />
                }
              </div>
            </div>

            @let summary = summaryFor(po);
            @if (failed()) {
              <p-message severity="warn" styleClass="w-full">
                {{ 'ApDocument_ReceivedCoverage_LoadFailed' | translateWF: 'The receipt detail could not be loaded.' }}
              </p-message>
            } @else if (summary) {
              <!-- Items: always in PO currency, the ledger's own basis, even when the check above is functional. -->
              <h3 class="section-title">{{ 'ApDocument_ReceivedCoverage_Items' | translateWF: 'Items' }} ({{ currencyLabel(summary.currency) }})</h3>
              <p-table [value]="summary.items" styleClass="mb-4">
                <ng-template pTemplate="header">
                  <tr>
                    <th>{{ 'ApDocument_ReceivedCoverage_Item' | translateWF: 'Item' }}</th>
                    <th class="text-right">{{ 'ApDocument_ReceivedCoverage_Ordered' | translateWF: 'Ordered' }}</th>
                    <th class="text-right">{{ 'ApDocument_ReceivedCoverage_ReceivedQty' | translateWF: 'Received' }}</th>
                    <th class="text-right">{{ 'ApDocument_ReceivedCoverage_Returned' | translateWF: 'Returned' }}</th>
                    <th class="text-right">{{ 'ApDocument_ReceivedCoverage_UnitPrice' | translateWF: 'Unit price (pre-tax)' }}</th>
                    <th class="text-right">{{ 'ApDocument_ReceivedCoverage_NetValue' | translateWF: 'Received value' }}</th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-item>
                  <tr>
                    <td class="min-w-0">
                      <!-- The code always shows: it identifies the item even when the name did not resolve. -->
                      <span class="mr-1.5 text-[var(--p-text-muted-color)]">({{ item.itemCode }})</span>
                      @let name = itemName(item.itemCode);
                      @if (name) {
                        <span [title]="name">{{ name }}</span>
                      }
                    </td>
                    <td class="text-right tabular-nums">{{ item.orderedQuantity | number: '1.0-2' }}</td>
                    <td class="text-right tabular-nums">{{ item.receivedQuantity | number: '1.0-2' }}</td>
                    <td class="text-right tabular-nums">{{ item.returnedQuantity | number: '1.0-2' }}</td>
                    <td class="text-right tabular-nums">{{ item.unitPrice | number: '1.2-4' }}</td>
                    <td class="text-right tabular-nums">{{ item.netReceivedAmount | number: '1.2-2' }}</td>
                  </tr>
                </ng-template>
              </p-table>

              <h3 class="section-title">{{ 'ApDocument_ReceivedCoverage_Receipts' | translateWF: 'Receiving documents' }}</h3>
              <p-table [value]="summary.receipts" styleClass="mb-4">
                <ng-template pTemplate="header">
                  <tr>
                    <th>{{ 'ApDocument_ReceivedCoverage_Document' | translateWF: 'Document' }}</th>
                    <th>{{ 'ApDocument_ReceivedCoverage_Type' | translateWF: 'Type' }}</th>
                    <th>{{ 'ApDocument_ReceivedCoverage_Date' | translateWF: 'Date' }}</th>
                    <th class="text-right">{{ 'ApDocument_ReceivedCoverage_Value' | translateWF: 'Value' }}</th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-receipt>
                  <tr>
                    <td>{{ receipt.sourceNumber ?? '—' }}</td>
                    <td>
                      @switch (receipt.sourceEntityDefinitionCode) {
                        @case (SourceType.GoodsReceipt) {
                          <p-tag severity="success" [value]="'ApDocument_ReceivedCoverage_Grn' | translateWF: 'Goods receipt'" />
                        }
                        @case (SourceType.Shipment) {
                          <p-tag severity="success" [value]="'ApDocument_ReceivedCoverage_Shipment' | translateWF: 'Shipment'" />
                        }
                        @case (SourceType.ProviderReturn) {
                          <p-tag severity="danger" [value]="'ApDocument_ReceivedCoverage_Return' | translateWF: 'Provider return'" />
                        }
                      }
                    </td>
                    <!-- 'UTC' is load-bearing: a received date is a calendar day, and a UTC-6 browser would show the day before (#5234). -->
                    <td>{{ receipt.receivedDate | date: 'dd/MM/yyyy' : 'UTC' }}</td>
                    <td class="text-right tabular-nums">{{ receipt.effect < 0 ? '−' : '' }}{{ receipt.amount | number: '1.2-2' }}</td>
                  </tr>
                </ng-template>
                <ng-template pTemplate="emptymessage">
                  <tr>
                    <td colspan="4">{{ 'ApDocument_ReceivedCoverage_NoReceipts' | translateWF: 'Nothing has been received on this PO yet.' }}</td>
                  </tr>
                </ng-template>
              </p-table>
            }

            @if (othersInBatch(po).length > 0 || paidBefore(po).length > 0) {
              <h3 class="section-title">{{ 'ApDocument_ReceivedCoverage_Documents' | translateWF: 'AP documents on this PO' }} ({{ unit }})</h3>
              <p-table [value]="po.documents">
                <ng-template pTemplate="header">
                  <tr>
                    <th>{{ 'ApDocument_ReceivedCoverage_Document' | translateWF: 'Document' }}</th>
                    <th class="text-right">{{ 'ApDocument_ReceivedCoverage_PaidToDate' | translateWF: 'Paid to date' }}</th>
                    <th class="text-right">{{ 'ApDocument_ReceivedCoverage_PayingNow' | translateWF: 'Paying now' }}</th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-doc>
                  <tr>
                    <td>
                      {{ doc.documentNumber ?? '—' }}
                      @if (doc.fiscalNumber) {
                        <span class="text-[var(--p-text-muted-color)]"> · {{ doc.fiscalNumber }}</span>
                      }
                      @if (doc.isThisDocument) {
                        <p-tag class="ml-2" severity="info" [value]="'ApDocument_ReceivedCoverage_ThisOne' | translateWF: 'This document'" />
                      } @else if (doc.inThisBatch) {
                        <p-tag class="ml-2" severity="secondary" [value]="'ApDocument_ReceivedCoverage_InBatch' | translateWF: 'Also being paid now'" />
                      }
                    </td>
                    <td class="text-right tabular-nums">{{ doc.paidToDate | number: '1.2-2' }}</td>
                    <td class="text-right tabular-nums">{{ doc.payingNow | number: '1.2-2' }}</td>
                  </tr>
                </ng-template>
              </p-table>
            }
          }
        </p-tabpanel>
      }
    </p-tabpanels>
  </p-tabs>
</section>

<footer>
  <p-button class="w-full" [label]="'Close' | translateWF: 'Close'" variant="outlined" (click)="onClose()" />
</footer>
